import { computed, inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { RsOverviewServerSideStateManager } from '@rs-shared-lib/abstract-classes/rs-overview-state/rs-overview-server-side-state-manager';
import { RsPersistOverviewState } from '@rs-shared-lib/abstract-classes/rs-overview-state/persistency/rs-persist-overview-state.decorator';
import { rsCountFilters } from '@rs-shared-lib/utils/count-filters/count-filters';
import { ServerSideQueryParams } from '@rs-shared-lib/abstract-classes/rs-overview-state/models/rs-overview-server-side-state-manager';
import { UserService } from '@core/services/user.service';
import { environment } from '@environments/environment';
import { MRT_DEFAULT_PAGE_SIZE, MRT_PAGE_SIZE_OPTIONS } from '@shared/constants/mrt-page-size-options';
import { SupplierOverview } from '@shared/models/suppliers/supplier-overview';
import { RsUtils } from '@lib/rs-ngx';

export type SupplierContractStatus = 'NO_CONTRACT' | 'ACTIVE_CONTRACT' | 'INACTIVE_CONTRACT' | null;

export type SuppliersFilters = {
  supplierStatus: SupplierContractStatus;
  brandId: number | null;
  city: string;
  invoiceName: string;
  postalCode: number | null;
  supplierGroup: string;
  supplierName: string;
  supplierNumber: string;
  supplierReference: string;
  vatNumber: string;
};

function mapToActive(supplierStatus: SupplierContractStatus): boolean | undefined {
  switch (supplierStatus) {
    case 'ACTIVE_CONTRACT':
      return true;
    case 'INACTIVE_CONTRACT':
      return false;
    default:
      return undefined;
  }
}

function mapToHasSupplierContract(supplierStatus: SupplierContractStatus): boolean | undefined {
  switch (supplierStatus) {
    case 'ACTIVE_CONTRACT':
    case 'INACTIVE_CONTRACT':
      return true;
    case 'NO_CONTRACT':
      return false;
    default:
      return undefined;
  }
}

@RsPersistOverviewState({
  storageKeySuffix: 'MrtLeasingCompanySuppliers',
  includeUserContext: () => inject(UserService).getCurrentUser()!.loginString,
})
@Injectable()
export class SuppliersOverviewStateService extends RsOverviewServerSideStateManager<SupplierOverview, SuppliersFilters> {
  public readonly filterCount = computed(() => rsCountFilters(this.filters()));

  constructor() {
    const http = inject(HttpClient);

    super({
      serviceMethod: (params: ServerSideQueryParams<SuppliersFilters>) => {
        const { supplierStatus, ...restFilters } = params;

        return http.get<SupplierOverview[]>(
          `${environment.apiUrl}/leasing-company/supplier-overview`,
          {
            params: RsUtils.trimEmptyHttpParams({
              ...restFilters,
              active: mapToActive(supplierStatus),
              hasSupplierContract: mapToHasSupplierContract(supplierStatus),
            }),
            observe: 'response',
          },
        );
      },
      initialSortingState: { active: 'SUPPLIER_NAME', direction: 'asc' },
      initialFiltersState: {
        supplierStatus: null,
        brandId: null,
        city: '',
        invoiceName: '',
        postalCode: null,
        supplierGroup: '',
        supplierName: '',
        supplierNumber: '',
        supplierReference: '',
        vatNumber: '',
      },
      initialPagination: { pageIndex: 0, pageSize: MRT_DEFAULT_PAGE_SIZE },
      config: { pageSizeOptions: MRT_PAGE_SIZE_OPTIONS },
    });
  }
}
